import { CookieConsent } from "@trustpilot/businessapp-browserstorage";
import { motion, AnimatePresence } from "framer-motion";
import { Text } from "ilenia";
import React from "react";
import { useHistory } from "react-router-dom";

import { trackingConst } from "../../constants";
import { useAnalytics } from "../../utils/analytics";
import { changeBusinessUnit, setSessionStorage } from "../../utils/changeAccount";
import { SSOAUTHENTICATED_BUSINESSACCOUNTID_COOKIE, getCookieByName } from "../../utils/cookies";
import { logout } from "../../utils/logout";
import BusinessUnitDropdown from "./businessUnitDropdown";
import Icons from "./icons";
import {
  StyledBusinessUnitSelector,
  StyledItemSelector,
  StyledSelectedItemCard,
  StyledSelectedItemCardIcon,
  StyledSelectedItemCardInfo,
  StyledSelectedItemCardLock,
  StyledSelectedItemCardSwitch,
  StyledSelectedItemCardArrowRight,
  StyledProfileLink,
  StyledProfileIcon,
  StyledBusinessUnitSelectorWrapper,
} from "./styled";
import * as Types from "./types";

const SelectedItemCard: React.FC<Types.SelectedItemCard> = ({ unitName, showLock, type, isOpen }) => (
  <StyledSelectedItemCard title={unitName}>
    <StyledSelectedItemCardIcon>{type === "account" ? <Icons.Company /> : <Icons.Domain />}</StyledSelectedItemCardIcon>
    <StyledSelectedItemCardInfo>
      <StyledSelectedItemCardSwitch>
        <Text id={`BusinessAccountSelector.switch${type}`} />
      </StyledSelectedItemCardSwitch>
    </StyledSelectedItemCardInfo>
    {showLock && (
      <StyledSelectedItemCardLock>
        <Icons.Lock />
      </StyledSelectedItemCardLock>
    )}
    <StyledSelectedItemCardArrowRight>
      <Icons.ArrowDown isOpen={isOpen} />
    </StyledSelectedItemCardArrowRight>
  </StyledSelectedItemCard>
);

const ItemSelector: React.FC<Types.ItemSelector> = ({
  changeSelectedUnit,
  selectedId,
  type,
  unitList,
  unitName,
  active,
  updateActiveSelector,
  showLock,
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const variants = {
    expand: "EXPAND",
    collapse: "COLLAPSE",
  };

  return (
    <StyledItemSelector
      onClick={() => {
        updateActiveSelector((state) => (state === type ? "" : type));
        setIsOpen(!isOpen);
      }}
    >
      <SelectedItemCard type={type} unitName={unitName} showLock={showLock} isOpen={isOpen} />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={variants.collapse}
            exit={variants.collapse}
            animate={isOpen ? variants.expand : variants.collapse}
            variants={{
              [variants.expand]: {
                height: "auto",
                transition: { height: { duration: 0.3, ease: "easeInOut" } },
              },
              [variants.collapse]: {
                height: 0,
                transition: { height: { duration: 0.3, ease: "easeInOut" } },
              },
            }}
          >
            <BusinessUnitDropdown
              changeSelectedUnit={changeSelectedUnit}
              selectedId={selectedId}
              type={type}
              unitList={unitList}
              active={active}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </StyledItemSelector>
  );
};

const makeSortedList = (list: Types.BusinessUnitDropdownItem[], key: string[]) =>
  [...list].sort((itemA, itemB) => {
    const getVal = (item) => key.reduce((acc, cur) => acc[cur], item);
    const a = getVal(itemA).toLowerCase();
    const b = getVal(itemB).toLowerCase();

    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  });

const BusinessUnitSelector: React.FC<Types.BusinessUnitSelector> = ({
  businessAccounts,
  selectedBusinessAccountName,
  businessUnits,
  closeSelector,
  currentBusinessUnit,
  selectedBusinessAccountId,
  showBusinessUnitConsents,
  isTrustpilotEmployee,
  publicProfileUrl,
  canManageProfile,
}) => {
  const analytics = useAnalytics();
  const history = useHistory();
  const [activeSelector, updateActiveSelector] = React.useState<string>("");

  const showLockOnAccountItem =
    showBusinessUnitConsents && businessUnits.length === 1 && !businessUnits[0].hasAccountManagementConsent;

  const showLockOnDomainItem = showBusinessUnitConsents && !currentBusinessUnit.hasAccountManagementConsent;

  const businessUnitsTransformed = businessUnits.map(({ identifyingName, id, hasAccountManagementConsent }) => ({
    name: identifyingName,
    id,
    isItemLocked: showBusinessUnitConsents && !hasAccountManagementConsent,
  }));

  const businessAccountsTransformed = businessAccounts.map(
    ({ name, id, ssoIsEnforced }): Types.BusinessUnitDropdownItem => ({
      name,
      id,
      ssoIsEnforced,
    }),
  );

  const onLinkClick = React.useCallback(
    (e) => {
      e.preventDefault();
      analytics.track(trackingConst.events.linkClicked, {
        context: trackingConst.contexts.accountSelector,
        name: "setup-public-profile",
        placement: "header",
        target: e.target.href,
      });
      closeSelector();
      const path = new URL(e.target.href);
      history.push(path.pathname);
    },
    [publicProfileUrl, analytics],
  );

  const onPublicProfileClick = React.useCallback(() => {
    analytics.track(trackingConst.events.linkClicked, {
      action: "Go to public profile",
      context: trackingConst.contexts.accountSelector,
      name: trackingConst.names.goToPublicProfileConsumerSite,
      target: "https://www.trustpilot.com/",
    });
    closeSelector();
  }, [analytics]);

  const onBusinessAccountChange = React.useCallback(
    (account: Types.BusinessUnitDropdownItem, previouslySelectedAccountId: string) => {
      const {
        id,
        name,
        businessUnits: [firstUnit],
        ssoIsEnforced,
      } = businessAccounts.find(({ id }) => id === account.id);
      const ssoAuthenticatedBusinessAccountId = getCookieByName(SSOAUTHENTICATED_BUSINESSACCOUNTID_COOKIE);

      CookieConsent.hasCategoryConsent("C0002")
        .then((hasTrackingConsent) => {
          if (hasTrackingConsent) {
            analytics.track(trackingConst.events.accountSelected, {
              context: trackingConst.contexts.accountSelector,
              account: {
                id,
                name,
              },
            });
          }
        })
        .finally(() => {
          setSessionStorage();
          // If user is not Trustpilot employee, they did not sign in with SSO on the selected business account and that same business account has SSO enforced, then redirect to /sso-login page
          if (
            ssoIsEnforced &&
            previouslySelectedAccountId !== id &&
            !isTrustpilotEmployee &&
            ssoAuthenticatedBusinessAccountId !== id
          ) {
            return logout({}, "/sso-login");
          } else {
            changeBusinessUnit(firstUnit);
          }
        });

      closeSelector();
    },
    [businessAccounts, closeSelector, analytics],
  );

  const onBusinessUnitChange = React.useCallback(
    (unit: Types.BusinessUnitDropdownItem) => {
      const newUnit = businessUnits.find(({ id }) => id === unit.id);

      CookieConsent.hasCategoryConsent("C0002")
        .then((hasTrackingConsent) => {
          if (hasTrackingConsent) {
            analytics.track(trackingConst.events.businessUnitSelected, {
              context: trackingConst.contexts.businessUnitSelector,
              accountsEnabled: businessAccounts.length > 1,
              businessUnit: {
                id: newUnit.id,
                displayName: newUnit.displayName,
              },
            });
          }
        })
        .finally(() => {
          setSessionStorage();
          changeBusinessUnit(newUnit.id);
        });

      closeSelector();
    },
    [businessUnits, closeSelector, analytics],
  );

  return (
    <StyledBusinessUnitSelector data-key="BusinessUnitSelector">
      <StyledBusinessUnitSelectorWrapper>
        {businessAccountsTransformed.length > 1 && (
          <ItemSelector
            type="account"
            unitName={selectedBusinessAccountName}
            unitList={makeSortedList(businessAccountsTransformed, ["name"])}
            selectedId={selectedBusinessAccountId}
            changeSelectedUnit={onBusinessAccountChange}
            active={activeSelector === "account"}
            updateActiveSelector={updateActiveSelector}
            showLock={showLockOnAccountItem}
          />
        )}
        {businessUnitsTransformed.length > 1 && (
          <ItemSelector
            type="domain"
            unitName={currentBusinessUnit.name.identifying}
            unitList={makeSortedList(businessUnitsTransformed, ["name"])}
            selectedId={currentBusinessUnit.id}
            changeSelectedUnit={onBusinessUnitChange}
            active={activeSelector === "domain"}
            updateActiveSelector={updateActiveSelector}
            showLock={showLockOnDomainItem}
          />
        )}
        {canManageProfile !== false && (
          <StyledItemSelector>
            <StyledProfileLink href="/company-settings/" onClick={onLinkClick}>
              <React.Fragment>
                <StyledProfileIcon>
                  <Icons.Profile />
                </StyledProfileIcon>
                <Text id="BusinessAccountSelector.setupprofilelink" />
              </React.Fragment>
            </StyledProfileLink>
          </StyledItemSelector>
        )}
        {publicProfileUrl && (
          <StyledItemSelector>
            <StyledProfileLink isExternal href={publicProfileUrl} target="_blank" onClick={onPublicProfileClick}>
              <React.Fragment>
                <StyledProfileIcon>
                  <Icons.ExternalProfile />
                </StyledProfileIcon>
                <Text id="BusinessAccountSelector.externalprofilelink" />
              </React.Fragment>
            </StyledProfileLink>
          </StyledItemSelector>
        )}
      </StyledBusinessUnitSelectorWrapper>
    </StyledBusinessUnitSelector>
  );
};

export default BusinessUnitSelector;
